import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatChipInputEvent } from '@angular/material/chips';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MY_DATE_FORMATS, MyDateAdapter } from '@material';
import { BroadcasterService } from 'ng-broadcaster';
import { QueryBuilderConfig } from 'ng-query-builder';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { CommonUtilsService } from '../../services/commonutils/common-utils.service';

@Component({
    selector: 'app-query-builder-new',
    templateUrl: './query-builder-new.component.html',
    styleUrls: ['./query-builder-new.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MyDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: MY_DATE_FORMATS,
        },
    ],
    standalone: false,
})
export class QueryBuilderNewComponent implements OnInit, OnDestroy {
    @Input() query;
    @Input() config: QueryBuilderConfig;
    @Input() allowRuleset;
    @Input() isAddRuleVisible: boolean;
    @Input() allowCollapse: boolean;
    @Input() relationConfigExists: boolean;
    @Input() isRelationDisabled: boolean;
    @Input() origin: string;
    @Input() queryType: 'SEARCH' | 'SORT';
    @Input() queryConfigChange: Subject<{
        searchConfig?: QueryBuilderConfig;
        sortConfig?: QueryBuilderConfig;
    }>;
    @Input() disabled: boolean;
    @Input() isTaskInitiateProcess: boolean;
    @Input() appVsBusinesskeys: any;

    @Output() queryChanged = new EventEmitter<any>();
    @Output() relationChanged = new EventEmitter<any>();

    customOperatorsMap: { [key: string]: string[] } = {
        string: ['EQ', 'NOT_EQUALS', 'IN', 'CONTAINS', 'is null', 'is not null'],
        number: ['EQ', 'NOT_EQUALS', 'IN', 'LT', 'LTE', 'GT', 'GTE', 'is null', 'is not null'],
        time: ['EQ', 'NOT_EQUALS', 'IN', 'LT', 'LTE', 'GT', 'GTE', 'is null', 'is not null'],
        date: ['EQ', 'NOT_EQUALS', 'IN', 'LT', 'LTE', 'GT', 'GTE', 'is null', 'is not null'],
        category: ['EQ', 'NOT_EQUALS', 'IN', 'is null', 'is not null'],
        boolean: ['EQ', 'NOT_EQUALS', 'is null', 'is not null'],
    };
    addOnBlur = true;
    separatorKeysCodes = [ENTER, COMMA];
    queryCopy: any;
    unSubscribe = new Subject<void>();
    public searchField: string;
    public selectedAppId: string;

    constructor(private broadcaster: BroadcasterService) {}

    onParentChange = () => {
        // console.log('On Parent Change');
        if (this.origin !== 'ENCOLLAB') {
            const clone = CommonUtilsService.cloneObject(this.query);
            this.queryChanged.emit(clone);
        }
    };

    onOperatorChange = (operator: string, rule: any) => {
        if (operator === 'IN') {
            rule.value = [];
        } else {
            rule.value = '';
        }
        this.onParentChange();
    };

    addNewTag = (event: MatChipInputEvent, rule: any): void => {
        const input = event.input;
        const value = event.value;
        if ((value || '').trim()) {
            // Guard: rule.value may be undefined if the rule was loaded from saved criteria
            // without going through onOperatorChange (which initializes it to []).
            if (!Array.isArray(rule.value)) {
                rule.value = [];
            }
            rule.value.push(value.trim());
        }
        if (input) {
            input.value = '';
        }
        this.onParentChange();
    };

    removeTag = (value: string, rule: any): void => {
        const index = rule.value.indexOf(value);
        if (index >= 0) {
            rule.value.splice(index, 1);
        }
    };

    openPanel = () => {
        this.query['collapsed'] = false;
    };

    trackByOptionMethod(_index: number, option: any): any {
        return option.value;
    }

    trackByOperatorsMethod(_index: number, operator: any): any {
        return operator;
    }

    trackByEntityMethod(_index: number, entity: any): any {
        return entity.name;
    }

    trackByFieldMethod(index: number): any {
        return index;
    }

    getAppId(rule: any): string {
        if (rule.customAppId) return rule.customAppId;
        if (rule.value && typeof rule.value === 'string' && rule.value.includes('.appName == ')) {
            const match = rule.value.match(/\.appName == \\?"([^\\"]+)\\?"/);
            if (match && match[1]) {
                rule.customAppId = match[1];
                return rule.customAppId;
            }
        }
        return '';
    }

    isInstanceField(rule: any): boolean {
        const instanceFieldIds = ['instanceState.raw', 'instanceStatus.raw', 'validationStatus'];
        return instanceFieldIds.includes(rule.field);
    }

    getBusinessKeys(rule: any): string[] {
        if (rule.customBusinessKeys) return rule.customBusinessKeys;
        if (rule.value && typeof rule.value === 'string' && rule.value.includes('.attribute.')) {
            const match = rule.value.match(/\), \((.+?)\)\)}/);
            if (match && match[1]) {
                const attrs = match[1].split('+');
                rule.customBusinessKeys = attrs.map(attr => attr.replace('.attribute.', '').trim());
                return rule.customBusinessKeys;
            }
        }
        return [];
    }

    onAppChange = (event, rule) => {
        rule.customAppId = event.value;
        rule.customBusinessKeys = [];
        this.updateRuleValue(rule);
    };

    onBusinessKeysChange = (event: any, rule: any, selectRef?: MatSelect) => {
        rule.customBusinessKeys = event.value;
        this.updateRuleValue(rule);
        selectRef?.close();
    };

    updateRuleValue(rule: any) {
        if (rule.customAppId && rule.customBusinessKeys && rule.customBusinessKeys.length > 0) {
            const attributes = rule.customBusinessKeys.map(value => `.attribute.${value}`).join('+');
            rule.value = `{map(filter(events, .appName == "${rule.customAppId}"), (${attributes}))}`;
        } else {
            rule.value = '';
        }
        this.onParentChange();
    }

    ngOnInit() {
        this.broadcaster
            .on('queryConfigChange')
            .pipe(takeUntil(this.unSubscribe))
            .subscribe((data: { searchConfig: QueryBuilderConfig; sortConfig: QueryBuilderConfig }) => {
                this.config = Object.assign({}, data.searchConfig);
            });
        this.queryConfigChange
            ?.pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined)
            )
            .subscribe((data: { searchConfig: QueryBuilderConfig; sortConfig: QueryBuilderConfig }) => {
                switch (this.queryType) {
                    case 'SEARCH':
                        this.config = Object.assign({}, data.searchConfig);
                        break;
                    case 'SORT':
                        this.config = Object.assign({}, data.sortConfig);
                        break;
                }
            });
    }

    ngOnDestroy() {
        this.unSubscribe.next();
        this.unSubscribe.complete();
    }

    onPaste(event: ClipboardEvent, rule: any) {
        event.preventDefault();
        const pastedText = event.clipboardData?.getData('text') || '';
        const formattedValues = pastedText
            .trim()
            .split(/[\t\n]+/g)
            .filter(v => v.trim() !== '');

        const input = event.target as HTMLInputElement;
        if (input) {
            input.value = '';
        }
        formattedValues.forEach(val => {
            const clearValue = val.trim() || '';
            if (clearValue) {
                rule.value.push(clearValue);
            }
        });
    }
}
